import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FiX, FiCamera, FiTrash2 } from 'react-icons/fi';
import { getFormErrorMessage } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import { roomsAPI } from '../../services/api';
import { Modal } from '../../design-system';

const IncidentForm = ({ incident, onSave, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [photoBase64, setPhotoBase64] = useState(incident?.photo_base64 || null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const isEditing = !!incident;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      room_number: incident?.room_number || '',
      description: incident?.description || '',
    },
  });

  useEffect(() => {
    if (incident?.photo_base64) {
      setPhotoPreview(`data:image/jpeg;base64,${incident.photo_base64}`);
    }
  }, [incident]);

  useEffect(() => {
    loadRooms();
  }, []);

  const loadRooms = async () => {
    try {
      const roomsData = await roomsAPI.getAll();
      // Sort rooms by room number
      const sortedRooms = roomsData.sort((a, b) => {
        const numA = parseInt(a.number) || 0;
        const numB = parseInt(b.number) || 0;
        return numA - numB;
      });
      setRooms(sortedRooms);
    } catch (error) {
      console.error('Error loading rooms:', error);
    } finally {
      setLoadingRooms(false);
    }
  };

  const handlePhotoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona una imagen válida');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no puede superar los 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      setPhotoPreview(base64);
      // Remove the data:image/...;base64, prefix for storage
      const base64Data = base64.split(',')[1];
      setPhotoBase64(base64Data);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setPhotoBase64(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      await onSave({
        ...data,
        photo_base64: photoBase64,
      });
    } catch (error) {
      console.error('Error saving incident:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="lg"
      showCloseButton={false}
    >
      <Modal.Header>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Editar Incidencia' : 'Nueva Incidencia'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" id="incident-form">
              {/* Room Number */}
              <div>
                <label htmlFor="room_number" className="form-label">
                  Número de habitación *
                </label>
                {loadingRooms ? (
                  <div className="form-input flex items-center justify-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2 text-sm text-gray-500">Cargando habitaciones...</span>
                  </div>
                ) : (
                  <select
                    id="room_number"
                    className={`form-input ${errors.room_number ? 'border-danger-500' : ''}`}
                    {...register('room_number', {
                      required: 'El número de habitación es requerido',
                    })}
                  >
                    <option value="">Selecciona una habitación</option>
                    {rooms.map((room) => (
                      <option key={room.id} value={room.number}>
                        {room.number}
                      </option>
                    ))}
                  </select>
                )}
                {errors.room_number && (
                  <p className="form-error">{getFormErrorMessage(errors.room_number)}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="form-label">
                  Descripción *
                </label>
                <textarea
                  id="description"
                  rows={4}
                  className={`form-input ${errors.description ? 'border-danger-500' : ''}`}
                  placeholder="Describe el problema o incidencia encontrada..."
                  {...register('description', {
                    required: 'La descripción es requerida',
                    minLength: {
                      value: 10,
                      message: 'La descripción debe tener al menos 10 caracteres'
                    },
                    maxLength: {
                      value: 1000,
                      message: 'La descripción no puede tener más de 1000 caracteres'
                    }
                  })}
                />
                {errors.description && (
                  <p className="form-error">{getFormErrorMessage(errors.description)}</p>
                )}
              </div>

              {/* Photo */}
              <div>
                <label className="form-label">
                  Foto (opcional)
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                {photoPreview ? (
                  <div className="relative">
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <FiTrash2 className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-primary-400 hover:text-primary-500 transition-colors"
                  >
                    <FiCamera className="h-8 w-8 mb-2" />
                    <span className="text-sm">Haz clic para añadir una foto</span>
                  </button>
                )}
              </div>

        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="incident-form"
          className="btn btn-primary flex items-center space-x-2"
          disabled={isSubmitting}
        >
          {isSubmitting && <LoadingSpinner size="sm" />}
          <span>{isEditing ? 'Actualizar' : 'Crear'}</span>
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default IncidentForm;